(() => {
  const pickerSelector = '[data-product-family-picker]';
  const linkSelector = '[data-product-family-link]';
  const maxCachedSections = 6;
  const maxViewportPrefetches = 2;
  const maxImagePreloads = 3;
  const sectionCache = new Map();
  const imagePreloads = new Map();
  const observedPickers = new WeakSet();
  let viewportSectionPrefetches = 0;
  let viewportImagePreloads = 0;
  let requestToken = 0;

  function cleanDuplicateAssets() {
    ['SakuraProductFamilyPickerStyles', 'SakuraProductFamilyPickerScript'].forEach((id) => {
      const assets = document.querySelectorAll(`#${id}`);
      assets.forEach((asset, index) => {
        if (index > 0) asset.remove();
      });
    });
  }

  function navigateNormally(url) {
    window.location.assign(url);
  }

  function viewportTop(element) {
    try {
      const top = element?.getBoundingClientRect?.().top;
      return Number.isFinite(top) ? top : null;
    } catch (error) {
      return null;
    }
  }

  function compensateScroll(previousTop, updatedTop) {
    const delta = updatedTop - previousTop;
    if (previousTop === null || updatedTop === null || !Number.isFinite(delta) || Math.abs(delta) < 1 || typeof window.scrollBy !== 'function') return;
    try {
      window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    } catch (error) {
      // Geometry and scrolling support must never turn a successful swap into navigation.
    }
  }

  function preserveLayoutHeights(currentSection, updatedSection) {
    ['.product__media-wrapper', '.product__info-container > .product__title'].forEach((selector) => {
      const currentElement = currentSection?.querySelector?.(selector);
      const updatedElement = updatedSection?.querySelector?.(selector);
      const currentHeight = currentElement?.getBoundingClientRect?.().height;
      if (!updatedElement || !Number.isFinite(currentHeight) || currentHeight <= 0) return;
      updatedElement.style.minHeight = `${Math.ceil(currentHeight)}px`;
    });
  }

  function setExpanded(picker, expanded) {
    const toggle = picker?.querySelector?.('[data-product-family-expand]');
    if (!picker || !toggle) return;
    picker.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? toggle.dataset.expandedLabel : toggle.dataset.collapsedLabel;
  }

  function refreshRelatedProducts(productId) {
    let recommendations;
    try {
      recommendations = document.querySelector('product-recommendations.related-products');
      if (!recommendations) return;
      if (!productId) throw new Error('The selected product ID is missing.');
      if (!recommendations.dataset.url) throw new Error('The recommendations URL is missing.');
      const requestUrl = new URL(recommendations.dataset.url, window.location.href);
      requestUrl.searchParams.set('product_id', productId);
      // A fresh element starts Dawn's lazy loader for the selected product.
      // Removing the old element also isolates any request it already started.
      const replacement = recommendations.cloneNode(false);
      replacement.dataset.url = requestUrl.href;
      recommendations.replaceWith(replacement);
    } catch (error) {
      // A failed recommendations refresh must not leave cards for the old product.
      try {
        recommendations?.remove();
      } catch (removeError) {
        // Recommendations are optional; keep the selected product visible.
      }
    }
  }

  function getSectionRequestUrl(destinationUrl, sectionId) {
    const requestUrl = new URL(destinationUrl, window.location.href);
    requestUrl.searchParams.set('section_id', sectionId);
    return requestUrl.href;
  }

  function cacheSection(requestUrl, sectionPromise) {
    sectionCache.delete(requestUrl);
    sectionCache.set(requestUrl, sectionPromise);
    while (sectionCache.size > maxCachedSections) {
      sectionCache.delete(sectionCache.keys().next().value);
    }
  }

  function seedCurrentSection(root = document) {
    const pickers = root.matches?.(pickerSelector) ? [root] : root.querySelectorAll(pickerSelector);
    pickers.forEach((picker) => {
      const sectionId = picker.dataset.sectionId;
      const currentLink = picker.querySelector(`${linkSelector}.is-current`);
      const section = sectionId && document.getElementById(`MainProduct-${sectionId}`);
      if (!currentLink || !section) return;
      const requestUrl = getSectionRequestUrl(currentLink.href, sectionId);
      if (sectionCache.has(requestUrl)) return;

      // Shopify replaces the pickup-availability template after it loads. Caching
      // that mutated custom element makes it throw when the section is restored.
      const initializedPickup = section.querySelector('pickup-availability[available]');
      if (initializedPickup && !initializedPickup.querySelector('template')) {
        getProductSection(currentLink.href, sectionId).catch(() => {});
        return;
      }

      cacheSection(requestUrl, Promise.resolve(section.outerHTML));
    });
  }

  async function getProductSection(destinationUrl, sectionId) {
    const requestUrl = getSectionRequestUrl(destinationUrl, sectionId);
    if (!sectionCache.has(requestUrl)) {
      cacheSection(
        requestUrl,
        fetch(requestUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        }).then((response) => {
          if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);
          return response.text();
        })
      );
    }

    const cachedSection = sectionCache.get(requestUrl);
    try {
      return await cachedSection;
    } catch (error) {
      if (sectionCache.get(requestUrl) === cachedSection) sectionCache.delete(requestUrl);
      throw error;
    }
  }

  function allowSpeculation() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !connection?.saveData && !/(^|-)2g$/.test(connection?.effectiveType || '');
  }

  function prefetchProductSection(link) {
    const picker = link?.closest(pickerSelector);
    const sectionId = picker?.dataset.sectionId;
    if (!picker || !sectionId || link.classList.contains('is-current') || !allowSpeculation()) return;
    getProductSection(link.href, sectionId).catch(() => {});
  }

  function preloadFeaturedImage(link, fromViewport = false) {
    if (!allowSpeculation() || !link || link.classList.contains('is-current')) return;
    if (fromViewport && viewportImagePreloads >= 1) return;
    if (imagePreloads.size >= maxImagePreloads) return;
    const source = link.dataset.featuredImageUrl;
    if (!source) return;
    const url = new URL(source, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol) || imagePreloads.has(url.href)) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.src = url.href;
    imagePreloads.set(url.href, image);
    if (fromViewport) viewportImagePreloads += 1;
  }

  function prefetchIntent(link) {
    prefetchProductSection(link);
    preloadFeaturedImage(link);
  }

  function scheduleViewportPrefetch(root = document) {
    if (!allowSpeculation()) return;
    const pickers = root.matches?.(pickerSelector) ? [root] : root.querySelectorAll(pickerSelector);
    pickers.forEach((picker) => {
      if (observedPickers.has(picker)) return;
      observedPickers.add(picker);
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const run = () => {
          const candidates = [...picker.querySelectorAll(`${linkSelector}:not(.is-current)`)]
            .filter((link) => !sectionCache.has(getSectionRequestUrl(link.href, picker.dataset.sectionId)))
            .slice(0, maxViewportPrefetches - viewportSectionPrefetches);
          if (candidates[0]) preloadFeaturedImage(candidates[0], true);
          candidates.forEach((link, index) => {
            viewportSectionPrefetches += 1;
            window.setTimeout(() => {
              if (picker.isConnected) prefetchProductSection(link);
            }, index * 150);
          });
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 1000 });
        } else {
          run();
        }
      }, { rootMargin: '120px 0px' });
      observer.observe(picker);
    });
  }

  document.addEventListener('pointerover', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  });
  document.addEventListener('focusin', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  });
  document.addEventListener('touchstart', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-product-family-expand]');
    if (!toggle) return;
    const picker = toggle.closest(pickerSelector);
    if (!picker) return;
    setExpanded(picker, toggle.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest?.(linkSelector);
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const picker = link.closest(pickerSelector);
    const sectionId = picker?.dataset.sectionId;
    const currentSection = sectionId && document.getElementById(`MainProduct-${sectionId}`);
    if (!picker || !currentSection || link.target || link.hasAttribute('download')) return;

    event.preventDefault();
    seedCurrentSection(picker);
    const token = ++requestToken;
    const destinationUrl = new URL(link.href, window.location.href).href;
    const selectedProductId = link.dataset.productId;
    const selectedProductTitle = link.dataset.productTitle;
    const currentProductTitle = picker.querySelector(`${linkSelector}.is-current`)?.dataset.productTitle;
    const titleSuffix = currentProductTitle && document.title.startsWith(currentProductTitle)
      ? document.title.slice(currentProductTitle.length)
      : '';
    const wasExpanded = picker.classList.contains('is-expanded');
    const loadingTimer = window.setTimeout(() => {
      if (token === requestToken && picker.isConnected) {
        picker.classList.add('is-loading');
        picker.setAttribute('aria-busy', 'true');
        link.classList.add('is-loading');
      }
    }, 180);

    try {
      const responseText = await getProductSection(destinationUrl, sectionId);
      const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const updatedSection = parsedDocument.getElementById(`MainProduct-${sectionId}`);
      const updatedLink = updatedSection?.querySelector(`${linkSelector}[data-product-id="${CSS.escape(selectedProductId)}"]`);
      if (!updatedSection || !updatedLink || token !== requestToken || !currentSection.isConnected) {
        throw new Error('The matching product section was not found.');
      }
      const previousLinkTop = viewportTop(link);
      const previousPickerTop = viewportTop(picker);
      preserveLayoutHeights(currentSection, updatedSection);
      document.querySelector('pickup-availability-drawer')?.remove();
      currentSection.replaceWith(updatedSection);
      if (wasExpanded) setExpanded(updatedSection.querySelector(pickerSelector), true);
      if (token === requestToken && updatedSection.isConnected && document.getElementById(`MainProduct-${sectionId}`) === updatedSection) {
        const updatedLinkTop = viewportTop(updatedLink);
        const useLink = previousLinkTop !== null && updatedLinkTop !== null;
        compensateScroll(
          useLink ? previousLinkTop : previousPickerTop,
          useLink ? updatedLinkTop : viewportTop(updatedSection.querySelector(pickerSelector))
        );
      }
      refreshRelatedProducts(selectedProductId);
      if (selectedProductTitle) document.title = `${selectedProductTitle}${titleSuffix}`;
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = destinationUrl;
      window.history.replaceState({}, '', destinationUrl);
      cleanDuplicateAssets();
      seedCurrentSection(updatedSection);
      scheduleViewportPrefetch(updatedSection);
      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      if (window.sakuraInitializeProductPurchaseGate) {
        window.sakuraInitializeProductPurchaseGate(updatedSection);
      }
      updatedLink.focus({ preventScroll: true });
    } catch (error) {
      if (token === requestToken) navigateNormally(destinationUrl);
    } finally {
      window.clearTimeout(loadingTimer);
      if (picker.isConnected) {
        picker.classList.remove('is-loading');
        picker.setAttribute('aria-busy', 'false');
        link.classList.remove('is-loading');
      }
    }
  });

  function initialize() {
    seedCurrentSection();
    scheduleViewportPrefetch();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
